import React, { useState } from "react";
import "./DatabaseImportPage.css";
import { API_BASE } from "../api/config";

const DatabaseImportPage = ({ setShowMappingPage, setRefreshTrigger }) => {
  const [loading, setLoading] = useState(false);
  const [kontoDaten, setKontoDaten] = useState(null);
  const [serverMessage, setServerMessage] = useState(null); // ✅ Nachricht vom Server speichern
  const [serverInfo, setServerInfo] = useState(null);       // komplette /konten-Antwort (status, konto, registriert, kontocode)
  // "DB-Eintrag einrichten"-Dialog (Konto in konto_code_map anlegen)
  const [showKontoDialog, setShowKontoDialog] = useState(false);
  const [kontoForm, setKontoForm] = useState({ kontocode: "", iban: "", beschreibung: "", objekt: "", nur_db: false });
  const [kontoDialogError, setKontoDialogError] = useState("");
  const [kontoDialogSaving, setKontoDialogSaving] = useState(false);

  const handleFetchKontoDaten = async () => {
    setLoading(true);
    setServerMessage(null);
    setServerInfo(null);
    setKontoDaten(null);

    try {
      const sessionId = localStorage.getItem("sessionId");
      if (!sessionId) {
        throw new Error("Keine gültige Sitzung gefunden.");
      }

      const response = await fetch(`${API_BASE}/database/konten?session=${sessionId}`);

      if (!response.ok) {
        throw new Error("Fehler beim Abrufen der Kontodaten.");
      }

      const data = await response.json();

      setShowMappingPage(true);
      setRefreshTrigger((prev) => prev + 1);
      setServerInfo(data);

      // Anlage-Dialog vorbefüllen (Kontocode aus Session/Registry, IBAN falls erkannt)
      setKontoForm((prev) => ({
        ...prev,
        kontocode: (data.kontocode || prev.kontocode || "").toUpperCase(),
        iban: data.konto || prev.iban || "",
      }));

      if (data.message) {
        setServerMessage(data.message); // ✅ Speichert Nachricht vom Server
      } else {
        setKontoDaten(data);
      }
    } catch (err) {
      setServerMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Button "Konto anlegen" zeigen, wenn das Konto unbekannt ist oder noch
  // keine Umsätze hat (bei ok nur, wenn der Kontocode-Eintrag fehlt)
  const zeigeAnlegenButton =
    serverInfo &&
    (serverInfo.status === "konto_unbekannt" ||
      serverInfo.status === "keine_umsaetze" ||
      (serverInfo.status === "ok" && !serverInfo.registriert));

  /* Konto anlegen: konto_code_map-Eintrag + Session-Konto setzen, danach
     Kontodaten erneut abrufen — der Import läuft dann normal weiter. */
  const handleKontoAnlegen = async () => {
    setKontoDialogError("");
    const kontocode = kontoForm.kontocode.trim().toUpperCase();
    const iban = kontoForm.iban.replace(/\s/g, "").toUpperCase();
    if (!kontocode) { setKontoDialogError("Bitte Kontocode/Objekt-Präfix angeben (z. B. 050, HIG)."); return; }
    if (!/^DE\d{20}$/.test(iban)) { setKontoDialogError("Bitte eine gültige deutsche IBAN angeben (DE + 20 Ziffern)."); return; }

    setKontoDialogSaving(true);
    try {
      // Duplikat-Warnung: existiert der Kontocode schon mit anderer IBAN?
      try {
        const check = await fetch(`${API_BASE}/database/konto_code_map`).then((r) => r.json());
        const vorhanden = (check.codes || check || []).find?.(
          (c) => (c.kontocode || "").toUpperCase() === kontocode
        );
        if (vorhanden && (vorhanden.iban || "").replace(/\s/g, "").toUpperCase() !== iban) {
          const ok = window.confirm(
            `Kontocode ${kontocode} existiert bereits mit IBAN ${vorhanden.iban}. Überschreiben?`
          );
          if (!ok) { setKontoDialogSaving(false); return; }
        }
      } catch { /* Prüfung optional — Anlage läuft trotzdem */ }

      // 1) Registry-Eintrag anlegen (ON DUPLICATE KEY UPDATE im Backend)
      const r1 = await fetch(`${API_BASE}/database/konto_code_map`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kontocode, iban, beschreibung: kontoForm.beschreibung.trim(), nur_db: !!kontoForm.nur_db }),
      });
      if (!r1.ok) {
        const j = await r1.json().catch(() => ({}));
        throw new Error(j.detail || `Kontocode konnte nicht gespeichert werden (HTTP ${r1.status}).`);
      }

      // 2) Optional: Objekt-Zuordnung (für die Konten-Gruppierung)
      const objekt = kontoForm.objekt.trim();
      if (objekt) {
        try {
          await fetch(`${API_BASE}/database/table/empfaenger_iban/row`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ data: { iban, objekt } }),
          });
        } catch { /* optional — Fehler hier blockiert die Konto-Anlage nicht */ }
      }

      // 3) Session-Konto setzen, damit der laufende Import direkt weitergeht
      const sessionId = localStorage.getItem("sessionId");
      const r2 = await fetch(`${API_BASE}/csv/resolve-konto`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Session-ID": sessionId || "" },
        body: JSON.stringify({ iban, kontocode }),
      });
      if (!r2.ok) {
        const j = await r2.json().catch(() => ({}));
        throw new Error(j.detail || `Session konnte nicht aktualisiert werden (HTTP ${r2.status}).`);
      }

      setShowKontoDialog(false);
      // 4) Kontodaten neu abrufen → Status wird keine_umsaetze/ok, Mapping-Flow läuft weiter
      await handleFetchKontoDaten();
    } catch (e) {
      setKontoDialogError(e.message);
    } finally {
      setKontoDialogSaving(false);
    }
  };

  return (
    <div className="database-import-page">
      <div className="import-container">
        <button className={`import-button ${loading ? "loading" : ""}`} onClick={handleFetchKontoDaten} disabled={loading}>
          {loading ? "🔄 Lade Daten..." : "📡 Datenbank abrufen"}
        </button>

        <div className="import-result">
          {serverMessage ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, maxWidth: 430 }}>
              <p className="server-message" style={{ margin: 0 }}>{serverMessage}</p>
              {serverInfo?.status === "keine_umsaetze" && serverInfo?.registriert && (
                <p style={{ fontSize: "0.85rem", margin: 0, textAlign: "center" }}>
                  Konto ist bereits als <strong>{serverInfo.kontocode}</strong> angelegt — es fehlen
                  nur noch die Umsätze. Unten Mapping speichern, dann werden die Daten importiert.
                </p>
              )}
              {zeigeAnlegenButton && (
                <button
                  className="import-button"
                  style={{ fontSize: "1em", padding: "10px 22px" }}
                  onClick={() => { setKontoDialogError(""); setShowKontoDialog(true); }}
                >
                  Konto anlegen
                </button>
              )}
            </div>
          ) : kontoDaten ? (
            <div>
              <table className="konto-table">
                <thead>
                  <tr>
                    <th>Konto</th>
                    <th>Anzahl Buchungen</th>
                    <th>Neuestes Buchungsdatum</th>
                    <th>Ältestes Buchungsdatum</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{kontoDaten.auftragskonto}</td>
                    <td>{kontoDaten.anzahl_buchungen}</td>
                    <td>{kontoDaten.neuestes_datum}</td>
                    <td>{kontoDaten.aeltestes_datum}</td>
                  </tr>
                </tbody>
              </table>
              {zeigeAnlegenButton && (
                <div style={{ marginTop: 10 }}>
                  <span style={{ fontSize: "0.85rem", marginRight: 10 }}>
                    Dieses Konto hat noch keinen Kontocode-Eintrag.
                  </span>
                  <button
                    className="import-button"
                    onClick={() => { setKontoDialogError(""); setShowKontoDialog(true); }}
                  >
                    Konto anlegen (DB-Eintrag einrichten)
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Dialog: Konto in der Registry anlegen */}
      {showKontoDialog && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5000 }}
          onClick={() => { if (!kontoDialogSaving) setShowKontoDialog(false); }}
        >
          <div
            style={{ background: "var(--bg-card, #fff)", color: "var(--color-gray-800, #222)", borderRadius: 10, padding: 22, width: 420, maxWidth: "92vw", boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 6px" }}>DB-Eintrag einrichten</h3>
            <p style={{ margin: "0 0 14px", fontSize: "0.85rem", color: "var(--color-gray-600, #666)" }}>
              Kontocode und IBAN werden gespeichert, damit dieses Konto ab jetzt
              automatisch erkannt wird.
            </p>
            {kontoDialogError && (
              <p style={{ color: "var(--color-error, #d32f2f)", fontSize: "0.85rem" }}>{kontoDialogError}</p>
            )}
            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Kontocode / Objekt-Präfix</label>
            <input
              style={{ width: "100%", padding: "8px 10px", marginBottom: 10, borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)" }}
              placeholder="z. B. 050, HIG, 005"
              value={kontoForm.kontocode}
              onChange={(e) => setKontoForm({ ...kontoForm, kontocode: e.target.value })}
            />
            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>IBAN</label>
            <input
              style={{ width: "100%", padding: "8px 10px", marginBottom: 10, borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)", fontFamily: "monospace" }}
              placeholder="DE00 0000 0000 0000 0000 00"
              value={kontoForm.iban}
              onChange={(e) => setKontoForm({ ...kontoForm, iban: e.target.value })}
            />
            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Beschreibung (optional)</label>
            <input
              style={{ width: "100%", padding: "8px 10px", marginBottom: 10, borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)" }}
              placeholder="Name des Kontos, z. B. Mietenkonto"
              value={kontoForm.beschreibung}
              onChange={(e) => setKontoForm({ ...kontoForm, beschreibung: e.target.value })}
            />
            <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Objekt (optional, für Gruppierung)</label>
            <input
              style={{ width: "100%", padding: "8px 10px", marginBottom: 16, borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)" }}
              placeholder="Objekt-Nummer, meist gleich dem Kontocode"
              value={kontoForm.objekt}
              onChange={(e) => setKontoForm({ ...kontoForm, objekt: e.target.value })}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem", marginBottom: 16, cursor: "pointer" }}
              title="CSV-Batch: Umsätze dieses Kontos gehen nur in die Datenbank, es wird keine UMS-Excel gesucht oder angelegt">
              <input
                type="checkbox"
                checked={!!kontoForm.nur_db}
                onChange={(e) => setKontoForm({ ...kontoForm, nur_db: e.target.checked })}
              />
              🗄 Nur DB (keine Excel) — Batch importiert CSVs dieses Kontos direkt in die Datenbank
            </label>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="import-button" onClick={handleKontoAnlegen} disabled={kontoDialogSaving}>
                {kontoDialogSaving ? "Speichere..." : "Anlegen und weiter"}
              </button>
              <button
                style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)", background: "transparent", cursor: "pointer", color: "inherit" }}
                onClick={() => setShowKontoDialog(false)}
                disabled={kontoDialogSaving}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseImportPage;
